import { useState, useEffect, useCallback } from 'react';
import { HealthStatus } from '../components/PlatformHealth/types';

type MetricKey = Exclude<HealthStatus, "HEALTHY">;

export interface DashboardFilters {
    activeStatusFilter: MetricKey | null;
    searchText: string;
    timeRange: string;
    customStartDate: Date | null;
    customEndDate: Date | null;
}

export interface DashboardSettings {
    autoRefreshInterval: number;
    filters: DashboardFilters;
    lastUpdated: string;
    version: string;
}

const STORAGE_KEY = 'platform_health_dashboard_settings';
const SETTINGS_VERSION = '1.0.0';

const DEFAULT_SETTINGS: DashboardSettings = {
    autoRefreshInterval: 5,
    filters: {
        activeStatusFilter: null,
        searchText: '',
        timeRange: '',
        customStartDate: null,
        customEndDate: null,
    },
    lastUpdated: new Date().toISOString(),
    version: SETTINGS_VERSION,
};

const parseDate = (dateStr: string | null): Date | null => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date;
};

const serializeSettings = (settings: DashboardSettings): string => {
    return JSON.stringify({
        ...settings,
        filters: {
            ...settings.filters,
            customStartDate: settings.filters.customStartDate?.toISOString() || null,
            customEndDate: settings.filters.customEndDate?.toISOString() || null,
        }
    });
};

const deserializeSettings = (json: string): DashboardSettings | null => {
    try {
        const parsed = JSON.parse(json);

        if (parsed.version !== SETTINGS_VERSION) {
            console.warn('Settings version mismatch, resetting to defaults');
            return null;
        }

        return {
            ...parsed,
            filters: {
                ...parsed.filters,
                customStartDate: parseDate(parsed.filters?.customStartDate),
                customEndDate: parseDate(parsed.filters?.customEndDate),
            }
        };
    } catch (error) {
        console.error('Error deserializing settings:', error);
        return null;
    }
};

export const usePersistentDashboardSettings = () => {
    const [settings, setSettings] = useState<DashboardSettings>(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const deserialized = deserializeSettings(saved);
                if (deserialized) {
                    return deserialized;
                }
            }
        } catch (error) {
            console.error('Error reading dashboard settings:', error);
        }
        return DEFAULT_SETTINGS;
    });

    const [isLoaded, setIsLoaded] = useState(false);

    const saveSettings = useCallback((newSettings: DashboardSettings) => {
        try {
            const serialized = serializeSettings(newSettings);
            localStorage.setItem(STORAGE_KEY, serialized);
        } catch (error) {
            console.error('Error saving dashboard settings:', error);
        }
    }, []);

    const updateSettings = useCallback((updates: Partial<DashboardSettings>) => {
        setSettings(prev => {
            const updated = {
                ...prev,
                ...updates,
                lastUpdated: new Date().toISOString()
            };
            saveSettings(updated);
            return updated;
        });
    }, [saveSettings]);

    const updateFilters = useCallback((filterUpdates: Partial<DashboardFilters>) => {
        setSettings(prev => {
            const updated = {
                ...prev,
                filters: {
                    ...prev.filters,
                    ...filterUpdates
                },
                lastUpdated: new Date().toISOString()
            };
            saveSettings(updated);
            return updated;
        });
    }, [saveSettings]);

    const updateAutoRefresh = useCallback((interval: number) => {
        updateSettings({ autoRefreshInterval: interval });
    }, [updateSettings]);

    const updateSearch = useCallback((searchText: string) => {
        updateFilters({ searchText });
    }, [updateFilters]);

    const updateTimeRange = useCallback((timeRange: string, startDate?: Date | null, endDate?: Date | null) => {
        updateFilters({
            timeRange,
            customStartDate: startDate || null,
            customEndDate: endDate || null,
        });
    }, [updateFilters]);

    const updateStatusFilter = useCallback((filter: MetricKey | null) => {
        updateFilters({ activeStatusFilter: filter });
    }, [updateFilters]);

    const resetToDefaults = useCallback(() => {
        setSettings({
            ...DEFAULT_SETTINGS,
            lastUpdated: new Date().toISOString()
        });
        saveSettings(DEFAULT_SETTINGS);
    }, [saveSettings]);

    useEffect(() => {
        setIsLoaded(true);
    }, []);

    return {
        settings,
        isLoaded,
        updateSettings,
        updateFilters,
        updateAutoRefresh,
        updateSearch,
        updateTimeRange,
        updateStatusFilter,
        resetToDefaults,

        // Convenience getters
        autoRefreshInterval: settings.autoRefreshInterval,
        filters: settings.filters,
        activeStatusFilter: settings.filters.activeStatusFilter,
        searchText: settings.filters.searchText,
        timeRange: settings.filters.timeRange,
        customStartDate: settings.filters.customStartDate,
        customEndDate: settings.filters.customEndDate,
    };
};